import React, { useEffect, useState } from "react";
import "@/App.css";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";
import {
  ArrowUpRight,
  ArrowRight,
  Check,
  Minus,
  Download,
  Linkedin,
  Mail,
  Star,
  Sparkles,
  TrendingUp,
  Target,
  Award,
  Shield,
  Globe,
  Search,
  BarChart3,
  AlertTriangle,
  ChevronRight,
  MapPin,
  Beaker,
  LogIn,
  Users,
  Cpu,
  Code2,
  HeartHandshake,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const FOUNDER_IMG =
  "https://customer-assets.emergentagent.com/job_b7ec6732-ddda-4855-9f74-a08614294f29/artifacts/3skf9vwq_Humera%20Rindani%20-%20Pic%20in%20Hijab.jpeg";
const HERO_BG =
  "https://images.pexels.com/photos/7703038/pexels-photo-7703038.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

const NAV = [
  { id: "founder", label: "Founder" },
  { id: "journey", label: "Journey" },
  { id: "voices", label: "Voices" },
  { id: "brands", label: "Brand House" },
  { id: "business", label: "Business" },
  { id: "financials", label: "Financials" },
  { id: "market", label: "Market" },
  { id: "valuation", label: "Valuation" },
  { id: "roadmap", label: "Roadmap" },
  { id: "risk", label: "Risk" },
  { id: "hiring", label: "Hiring" },
  { id: "contact", label: "Connect" },
];

function Header() {
  const navigate = useNavigate();
  return (
    <header className="crystal-header sticky top-0 z-50 print-hide" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="brand-mark">
          <div className="w-7 h-7 border border-[var(--bronze)] flex items-center justify-center">
            <span className="font-serif-display text-[var(--bronze)]" style={{ fontSize: 14 }}>B</span>
          </div>
          <div className="leading-tight">
            <div className="font-serif-display text-[15px] tracking-tight" style={{ color: "var(--ink)" }}>
              BEAUTWELLZ
            </div>
            <div className="overline" style={{ fontSize: 9 }}>Investor Brief · 2026</div>
          </div>
        </a>
        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <a
              key={n.id}
              href={`#${n.id}`}
              className="text-[12px] tracking-[0.12em] uppercase text-[var(--ink-2)] hover:text-[var(--ink)] transition-colors"
              style={{ fontFamily: "Outfit, sans-serif", fontWeight: 500 }}
              data-testid={`nav-${n.id}`}
            >
              {n.label}
            </a>
          ))}
        </nav>
        <button
          onClick={() => navigate("/login")}
          className="btn-ghost hidden md:inline-flex"
          data-testid="header-admin-button"
        >
          <LogIn size={14} /> Admin
        </button>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative grain hero-vignette overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center", filter: "saturate(0.7)" }}
        aria-hidden
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-end">
          <div className="md:col-span-7 reveal">
            <div className="overline mb-6">Founder &amp; Director · BEAUTWELLZ OPC PVT LTD</div>
            <h1 className="font-serif-display text-[44px] sm:text-[64px] md:text-[84px] leading-[0.95] tracking-tight" style={{ color: "var(--ink)" }}>
              Humera
              <br />
              <span style={{ fontStyle: "italic", color: "var(--bronze)" }}>Rindani</span>
              <span className="ml-3" style={{ color: "var(--ink)" }}>.</span>
            </h1>
            <p className="mt-6 max-w-xl text-[16px] md:text-[17px] leading-relaxed" style={{ color: "var(--ink-2)" }}>
              A recovery sabbatical became a vision. That vision became <strong style={{ color: "var(--ink)" }}>BEAUTWELLZ</strong> —
              a profitable, bootstrapped B2B glass &amp; packaging house serving Firmenich, ABAN Offshore &amp; Minati Essentials,
              now run entirely on her own <strong style={{ color: "var(--ink)" }}>AI-agent dashboard.</strong>
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a href="#valuation" className="btn-primary" data-testid="hero-investment-button">
                Investment Opportunity <ArrowUpRight size={14} />
              </a>
              <a href="#contact" className="btn-ghost" data-testid="hero-contact-button">
                Connect with Founder <ArrowRight size={14} />
              </a>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
              {[
                { v: "₹1Cr+", l: "Cumulative · FY20-26" },
                { v: "17 yrs", l: "R&D · Cosmetics + Hair" },
                { v: "4.3/5", l: "Verified Buyer Rating" },
              ].map((k) => (
                <div key={k.l} data-testid={`hero-stat-${k.l.split(" ")[0].toLowerCase()}`}>
                  <div className="metric-value text-[34px] md:text-[40px]" style={{ color: "var(--ink)" }}>{k.v}</div>
                  <div className="overline mt-1" style={{ color: "var(--ink-2)", fontSize: 10 }}>{k.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5 reveal image-zoom">
            <div className="relative aspect-[4/5] w-full bg-[var(--ivory-2)] overflow-hidden" style={{ border: "1px solid rgba(26,26,26,0.12)" }}>
              <img src={FOUNDER_IMG} alt="Humera Rindani, Founder & Director, BEAUTWELLZ OPC PVT LTD" className="w-full h-full object-cover" data-testid="founder-portrait" />
              <div className="absolute left-0 bottom-0 right-0 px-5 py-4 backdrop-blur-md" style={{ background: "rgba(247,245,240,0.78)" }}>
                <div className="overline" style={{ fontSize: 10 }}>Founder · Director · R&amp;D Specialist</div>
                <div className="font-serif-display text-[20px] leading-tight mt-1" style={{ color: "var(--ink)" }}>
                  Glass · Packaging · Private Label · Attar
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="kpi-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-wrap items-center gap-x-8 gap-y-3 text-[12px]" style={{ color: "var(--ink-2)", fontFamily: "Outfit, sans-serif", letterSpacing: "0.08em" }}>
          <span className="overline" style={{ fontSize: 10 }}>Memorandum Highlights</span>
          <span className="divider-dot" />
          <span>HQ Mumbai · Warehouse Vasai-Palghar · Office Tolichowki, Hyderabad</span>
          <span className="divider-dot" />
          <span>Telangana GST · Jan 2026</span>
          <span className="divider-dot" />
          <span>OpenVC-Listed · UAE GenAI Program-Eligible</span>
        </div>
      </div>
    </section>
  );
}

function Founder() {
  return (
    <section id="founder" className="py-24 md:py-32" data-testid="founder-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5 reveal">
            <div className="overline mb-4">The Operator</div>
            <h2 className="font-serif-display text-[36px] md:text-[52px] leading-[1.02] tracking-tight" style={{ color: "var(--ink)" }}>
              From recovery to <em style={{ color: "var(--bronze)" }}>R&amp;D Head</em> to founder — in 18 months.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 reveal">
            <p className="text-[16px] leading-relaxed mb-5" style={{ color: "var(--ink-2)" }}>
              Humera Rindani is the Founder &amp; Director of{" "}
              <strong style={{ color: "var(--ink)" }}>BEAUTWELLZ OPC PVT LTD</strong> —
              registered in Mumbai with an operational warehouse at Vasai,
              Palghar (Maharashtra), where she was born and raised. Cumulative
              revenue across <strong style={{ color: "var(--ink)" }}>FY 2020 – FY 2026</strong>:{" "}
              <strong style={{ color: "var(--ink)" }}>₹1 Crore+</strong> — solo, debt-free, unfunded.
            </p>
            <p className="text-[16px] leading-relaxed mb-5" style={{ color: "var(--ink-2)" }}>
              For 17 years before BEAUTWELLZ, Humera worked deep inside corporate
              Cosmetics &amp; Haircare R&amp;D. In{" "}
              <strong style={{ color: "var(--ink)" }}>May 2018</strong>, a health setback
              forced a <strong style={{ color: "var(--ink)" }}>planned recovery sabbatical</strong>.
              It was during that recovery — observing the daily mountain of hospital laundry — that the
              <strong style={{ color: "var(--ink)" }}> medical disposables vision was born</strong>.
              She returned stronger — promoted to{" "}
              <strong style={{ color: "var(--ink)" }}>R&amp;D Head — Haircare</strong>, and by{" "}
              <strong style={{ color: "var(--ink)" }}>February 2019</strong> was traveling India for
              <strong style={{ color: "var(--ink)" }}> Train-the-Trainer haircare programs</strong> and
              live <strong style={{ color: "var(--ink)" }}>stage demonstrations across Delhi, Kolkata &amp; Mumbai</strong>.
            </p>
            <p className="text-[16px] leading-relaxed mb-5" style={{ color: "var(--ink-2)" }}>
              That ability to rebuild — at a higher altitude than before — within months
              of stepping back is the same engine that has powered BEAUTWELLZ
              through six bootstrap years. Investors who dismissed the 2018 disposables
              vision watched COVID 2020 validate it; the disposables boom funded
              BEAUTWELLZ&apos;s first crores.
            </p>
            <p className="text-[16px] leading-relaxed" style={{ color: "var(--ink-2)" }}>
              Left her corporate role <strong style={{ color: "var(--ink)" }}>December 2019</strong>,
              integrated the vision by <strong style={{ color: "var(--ink)" }}>February 2020</strong>.
              In mid-2025 she relocated to Hyderabad and signed a rented office
              in <strong style={{ color: "var(--ink)" }}>Tolichowki</strong> in
              January 2026. With the freshly acquired Telangana GST, her{" "}
              <strong style={{ color: "var(--ink)" }}>D2C Attar Oil brand</strong>{" "}
              is queued behind one final variable: a Hyderabad operations hire.
            </p>

            <div className="grid grid-cols-2 gap-4 mt-8">
              {[
                { k: "Registered Office", v: "Mumbai · Maharashtra" },
                { k: "Warehouse", v: "Vasai · Palghar · MH (1 staff)" },
                { k: "Operating Office", v: "Tolichowki · Hyderabad (hiring)" },
                { k: "GST", v: "Maharashtra + Telangana" },
                { k: "R&D Depth", v: "17 yrs · Cosmetics + Hair" },
                { k: "Capital Stack", v: "Bootstrapped · Profitable" },
              ].map((r) => (
                <div key={r.k} className="card-flat p-4" data-testid={`founder-fact-${r.k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="overline" style={{ fontSize: 10 }}>{r.k}</div>
                  <div className="font-mono-data mt-1 text-[14px]" style={{ color: "var(--ink)" }}>{r.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Journey() {
  const chapters = [
    {
      n: "Pre-May 2018",
      t: "Corporate R&D · Foundations",
      d: "Several years inside corporate Cosmetics & Haircare R&D — Mumbai-based, formulating and developing products across the industry.",
      i: <Beaker size={14} />,
    },
    {
      n: "May 2018",
      t: "Vision · During Recovery",
      d: "A health setback forced a planned recovery sabbatical. During those months — observing the daily mountain of hospital laundry — the disposables vision was born.",
      i: <HeartHandshake size={14} />,
    },
    {
      n: "May 2018 – Jan 2019",
      t: "8 Months · Reset & Reframe",
      d: "Eight months of recovery and strategy. Sketching what would later become i-hydrate, i-dispose, i-biodegradable. Investors said the disposables idea wouldn't work. She replied: I'll do it myself.",
      i: <Sparkles size={14} />,
    },
    {
      n: "Jan – Dec 2019",
      t: "R&D Head · India Tours",
      d: "The comeback. Promoted to R&D Head — Haircare. By Feb 2019, traveling India for Train-the-Trainer programs and live stage demonstrations across Delhi, Kolkata & Mumbai. A higher altitude than before.",
      i: <Sparkles size={14} />,
    },
    {
      n: "Dec '19 — Feb '20",
      t: "The Leap",
      d: "Left her corporate role 10 Dec 2019. BEAUTWELLZ OPC GST incorporated 27 Feb 2020. First order 19 March 2020 — six days after India's lockdown began. The 2018 vision was already operational.",
      i: <Code2 size={14} />,
    },
    {
      n: "2020-22",
      t: "Vision Validated",
      d: "COVID arrived. Medical disposables became national-priority inventory. By 2023 BEAUTWELLZ had crossed ₹55L+ trading turnover from 10+ repeat clients — ABAN Offshore, MEDISAVE, Firmenich Aromatics, OGD Services (Essar Oils), Vinayak Traders, LIfEyelid, Minati Essentials, WildGlow Cosmetics, Syncpedia Technologies, Mapp Media, Perfume Valley, Fragrance Ocean & Samaq Attars — entirely no-credit.",
      i: <TrendingUp size={14} />,
    },
    {
      n: "2023-24",
      t: "Self-Taught Pivot",
      d: "Built the BEAUTWELLZ website herself with zero IT background. Pivoted as disposable margins normalised — into the higher-margin world of glass perfume bottles, cosmetic packaging and private-label execution (i-skills vertical).",
      i: <Code2 size={14} />,
    },
    {
      n: "2025-26",
      t: "AI-First Founder",
      d: "Hyderabad relocation. Tolichowki office. Telangana GST. Internal AI-agent dashboard for quoting, content, follow-ups & analytics — turning a one-woman company into an AI-leveraged operating system.",
      i: <Cpu size={14} />,
    },
  ];

  return (
    <section
      id="journey"
      className="py-24 md:py-32"
      data-testid="journey-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-7 reveal">
            <div className="overline mb-4">From Survival to Success · 2018 → 2026</div>
            <h2
              className="font-serif-display text-[36px] md:text-[52px] leading-[1.02] tracking-tight"
              style={{ color: "var(--ink)" }}
            >
              Vision seeded May 2018.
              <em style={{ color: "var(--bronze)" }}> Training India by Feb 2019. </em>
              Founding BEAUTWELLZ by 2020.
            </h2>
          </div>
          <div className="md:col-span-5 reveal flex items-end">
            <p className="text-[15px]" style={{ color: "var(--ink-2)" }}>
              Eight years. One operator. A vision seeded during a recovery sabbatical,
              validated by a pandemic, and now executed through an AI-agent dashboard.
              The pattern: rebuild faster, stronger, smarter — every time.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {chapters.map((c, i) => (
            <div
              key={c.n}
              className="card-flat p-7 reveal"
              style={{ transitionDelay: `${i * 60}ms` }}
              data-testid={`journey-${c.n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="flex items-center gap-2 mb-3" style={{ color: "var(--bronze)" }}>
                {c.i}
                <div className="overline">{c.n}</div>
              </div>
              <div className="font-serif-display text-[22px] leading-tight" style={{ color: "var(--ink)" }}>
                {c.t}
              </div>
              <p className="mt-3 text-[13px] leading-relaxed" style={{ color: "var(--ink-2)" }}>
                {c.d}
              </p>
            </div>
          ))}
        </div>

        <div
          className="mt-10 p-7 reveal"
          style={{
            background: "var(--onyx)",
            color: "var(--ivory)",
            border: "1px solid rgba(212,175,55,0.35)",
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <div className="overline" style={{ color: "var(--champagne)" }}>
                Why this matters to investors
              </div>
              <h3
                className="font-serif-display text-[26px] md:text-[32px] leading-tight mt-2"
                style={{ color: "var(--ivory)" }}
              >
                A founder who went from a recovery sabbatical to <em style={{ color: "var(--champagne)" }}>training India</em> in 8 months — and from there to building a profitable B2B brand investors had dismissed — is a known quantity for resilience under pressure.
              </h3>
            </div>
            <div className="md:col-span-5 grid grid-cols-2 gap-4">
              {[
                { v: "May 2018", l: "Vision · Genesis" },
                { v: "Feb 2019", l: "Comeback · India Tours" },
                { v: "Feb 2020", l: "BEAUTWELLZ Live" },
                { v: "AI-led", l: "Operations 2026" },
              ].map((m) => (
                <div key={m.l} className="p-3" style={{ border: "1px solid rgba(247,245,240,0.15)" }}>
                  <div className="metric-value text-[26px]" style={{ color: "var(--champagne)" }}>{m.v}</div>
                  <div className="overline mt-1" style={{ fontSize: 9, color: "rgba(247,245,240,0.6)" }}>{m.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BuyerVoices() {
  const voices = [
    {
      q: "Got protective face masks from BEAUTWELLZ when the entire chemist and medical market was selling at ₹40 per piece. Humera supplied us at ₹19.50 — nearly half — and on time, when no one else could deliver.",
      a: "Mr. Gurpreet Sara",
      r: "Essar Oils · OGD Services",
      m: "First-ever BEAUTWELLZ sale · ₹1.5L order · 2020",
    },
    {
      q: "Best price and widest variety of glass bottles we&apos;ve sourced from any Indian supplier — Humera knows her catalogue cold.",
      a: "Glass Bottle Buyer",
      r: "Verified Google Review · BEAUTWELLZ OPC PVT LTD",
      m: "One of 4.3/5-rated buyer testimonials",
    },
  ];
  return (
    <section
      id="voices"
      className="py-24 md:py-32"
      data-testid="voices-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="reveal mb-12">
          <div className="overline mb-4">Buyer Voices</div>
          <h2
            className="font-serif-display text-[36px] md:text-[52px] leading-[1.02] tracking-tight max-w-3xl"
            style={{ color: "var(--ink)" }}
          >
            The first sale tells the
            <em style={{ color: "var(--bronze)" }}> whole story.</em>
          </h2>
          <p className="mt-5 max-w-2xl text-[15px]" style={{ color: "var(--ink-2)" }}>
            BEAUTWELLZ&apos;s first invoice — ₹1.5 Lakh of protective masks to{" "}
            <strong style={{ color: "var(--ink)" }}>Essar Oils (OGD Services)</strong>{" "}
            at ₹19.50 / piece, when the chemist market was selling identical
            masks at ₹40. Fair pricing + reliable supply during the worst of
            COVID. Mr. Gurpreet Sara&apos;s review is publicly verifiable on the{" "}
            <strong style={{ color: "var(--ink)" }}>BEAUTWELLZ OPC PVT LTD</strong>{" "}
            Google Business profile.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {voices.map((v, i) => (
            <div
              key={i}
              className="card-flat p-8 reveal"
              style={{ transitionDelay: `${i * 80}ms` }}
              data-testid={`voice-${i}`}
            >
              <div
                className="font-serif-display text-[40px] leading-none"
                style={{ color: "var(--bronze)" }}
              >
                &ldquo;
              </div>
              <p
                className="font-serif-display text-[20px] md:text-[22px] leading-snug mt-2"
                style={{ color: "var(--ink)" }}
                dangerouslySetInnerHTML={{ __html: v.q }}
              />
              <div className="hairline mt-6 pt-5">
                <div className="font-mono-data text-[14px]" style={{ color: "var(--ink)" }}>
                  {v.a}
                </div>
                <div className="text-[12px]" style={{ color: "var(--ink-2)" }}>
                  {v.r}
                </div>
                <div className="overline mt-3" style={{ fontSize: 10 }}>
                  {v.m}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className="mt-10 p-7 reveal"
          style={{
            background: "rgba(212,175,55,0.08)",
            border: "1px solid rgba(156,124,56,0.3)",
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8">
              <div className="overline">The 2020 Price Differential</div>
              <h3
                className="font-serif-display text-[26px] md:text-[30px] leading-tight mt-1"
                style={{ color: "var(--ink)" }}
              >
                <em style={{ color: "var(--bronze)" }}>₹19.50</em> from Humera ·{" "}
                <span style={{ textDecoration: "line-through", color: "var(--ink-2)" }}>
                  ₹40.00
                </span>{" "}
                from the chemist market.
              </h3>
              <div className="text-[13px] mt-2" style={{ color: "var(--ink-2)" }}>
                51% below market price during a national-emergency demand spike. That&apos;s
                what a vision-led + R&amp;D-backed founder delivers when others are
                still pricing for panic.
              </div>
            </div>
            <div className="md:col-span-4 grid grid-cols-2 gap-3">
              <div className="p-3" style={{ background: "white", border: "1px solid rgba(26,26,26,0.08)" }}>
                <div className="overline" style={{ fontSize: 10 }}>BEAUTWELLZ</div>
                <div className="metric-value text-[26px]" style={{ color: "var(--bronze)" }}>₹19.50</div>
              </div>
              <div className="p-3" style={{ background: "white", border: "1px solid rgba(26,26,26,0.08)" }}>
                <div className="overline" style={{ fontSize: 10 }}>Market</div>
                <div className="metric-value text-[26px]" style={{ color: "var(--ink-2)" }}>₹40.00</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BrandHouse() {
  const brands = [
    {
      n: "i-hydrate",
      t: "Personal Hygiene Wipes",
      d: "Unisex personal &amp; intimate, baby wipes, adult / patient bath wipes — the gateway hygiene product range.",
    },
    {
      n: "i-dispose",
      t: "Medical Disposables",
      d: "Salon &amp; spa linens, PPE, surgical kits, nitrile gloves, cardiac kits — manufactured under ISO 14644 / 11135 / 11607 / 13485:2016 (TUV Rheinland).",
    },
    {
      n: "i-biodegradable",
      t: "15-Kit Lifestyle Range",
      d: "Baby Care · Patient Home Care · Hotel · Hostel · Pilgrimage · Railway · Daycare — 100% biodegradable linen kits. &quot;No more laundry days.&quot;",
    },
    {
      n: "i-skills",
      t: "Glass · Packaging · Perfumery",
      d: "R&amp;D-led sourcing of glass perfume bottles, cosmetic jars, droppers, pumps. Private-label execution. Start-to-finish brand build for indie beauty.",
    },
    {
      n: "i-construct",
      t: "Interior &amp; Build",
      d: "Family-legacy expertise — turnkey design &amp; construction for homes, salons, spas &amp; clinics. (BEAUTWELLZ&apos;s own 100-yr-old office restoration was the proof-of-craft.)",
    },
  ];
  const customers = [
    { billing: "ABAN Offshore PTE" },
    { billing: "MEDISAVE Health Pvt Ltd" },
    { billing: "Firmenich Aromatics &amp; Products Pvt Ltd" },
    { billing: "OGD Services", brand: "Essar Oils" },
    { billing: "Vinayak Traders" },
    { billing: "LIfEyelid" },
    { billing: "Minati Essentials", brand: "Perfume OEM" },
    { billing: "WildGlow Cosmetics Pvt Ltd", brand: "Red Wine Face Mask" },
    { billing: "Syncpedia Technologies", brand: "IK IK Perfumes" },
    { billing: "Mapp Media", brand: "Essents Perfume" },
    { billing: "Perfume Valley" },
    { billing: "Fragrance Ocean" },
    { billing: "Samaq Attars" },
    { billing: "Kings Group", brand: "The Bear House" },
    { billing: "Aarav Fragrance" },
  ];
  const certs = ["ISO Certified", "MSME Registered", "Startup India Certified", "IEC Holder", "Trade Mark · i-hydrate & i-dispose"];
  return (
    <section
      id="brands"
      className="py-24 md:py-32"
      style={{ background: "var(--ivory-2)" }}
      data-testid="brands-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-7 reveal">
            <div className="overline mb-4">The Brand House</div>
            <h2 className="font-serif-display text-[36px] md:text-[52px] leading-[1.02] tracking-tight" style={{ color: "var(--ink)" }}>
              Five verticals.
              <em style={{ color: "var(--bronze)" }}> One operator.</em>{" "}
              One vision.
            </h2>
          </div>
          <div className="md:col-span-5 reveal flex items-end">
            <p className="text-[15px]" style={{ color: "var(--ink-2)" }}>
              BEAUTWELLZ isn&apos;t a single-product company — it&apos;s a
              vision house. Each vertical sharpens a different revenue moat
              while sharing the same founder, the same R&amp;D backbone, and the
              same operating discipline.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {brands.map((b, i) => (
            <div
              key={b.n}
              className="card-flat p-6 reveal"
              style={{ transitionDelay: `${i * 60}ms` }}
              data-testid={`brand-${b.n}`}
            >
              <div
                className="font-serif-display text-[24px] tracking-tight"
                style={{ color: "var(--bronze)" }}
                dangerouslySetInnerHTML={{ __html: b.n }}
              />
              <div className="font-mono-data text-[13px] mt-2" style={{ color: "var(--ink)" }} dangerouslySetInnerHTML={{ __html: b.t }} />
              <p className="text-[12px] mt-3 leading-relaxed" style={{ color: "var(--ink-2)" }} dangerouslySetInnerHTML={{ __html: b.d }} />
            </div>
          ))}
        </div>

        <div className="mt-6 reveal p-6" style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(156,124,56,0.25)" }} data-testid="verticals-phasing-note">
          <div className="overline mb-2">Where this round goes · 15-year build plan</div>
          <p className="text-[13px] md:text-[14px] leading-relaxed" style={{ color: "var(--ink-2)" }}>
            <strong style={{ color: "var(--ink)" }}>i-skills</strong> (glass · packaging · perfumery) is the <strong style={{ color: "var(--ink)" }}>sole focus of this round</strong>.
            The other four verticals — <strong style={{ color: "var(--ink)" }}>i-dispose, i-hydrate, i-biodegradable and i-construct</strong> — are already
            <strong style={{ color: "var(--ink)" }}> R&amp;D-complete</strong> (all developed by Humera Rindani). They activate in phases <em>only after</em> the i-skills raise
            closes and the operating plan is set — each needs a dedicated team and domain experts, rolling out across a <strong style={{ color: "var(--ink)" }}>15-year build plan</strong>.
          </p>
        </div>

        {/* Customer trust strip */}
        <div className="mt-12 card-flat p-7 reveal" data-testid="customer-strip">
          <div className="overline mb-1">High-Paying Clients · Customised Services</div>
          <div className="overline mb-5" style={{ fontSize: 9, color: "var(--ink-2)" }}>Billing Name · Market Brand (where applicable)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
            {customers.map((c) => (
              <div key={c.billing}>
                <div
                  className="font-serif-display text-[18px] md:text-[20px]"
                  style={{ color: "var(--ink)" }}
                  dangerouslySetInnerHTML={{ __html: c.billing }}
                />
                {c.brand && (
                  <div className="text-[12px] mt-0.5" style={{ color: "var(--bronze)", fontFamily: "Outfit" }} dangerouslySetInnerHTML={{ __html: c.brand }} />
                )}
              </div>
            ))}
          </div>
          <div className="hairline mt-6 pt-4 flex flex-wrap gap-x-6 gap-y-2 text-[11px]" style={{ color: "var(--ink-2)", fontFamily: "Outfit", letterSpacing: "0.1em", textTransform: "uppercase" }}>
            <span>15+ active accounts</span>
            <span className="divider-dot" />
            <span>No-credit dispatch policy</span>
            <span className="divider-dot" />
            <span>Pan-India + GCC corridor</span>
            <span className="divider-dot" />
            <span>Invoices &amp; receipts on request</span>
          </div>
        </div>

        {/* Certifications + Mission */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mt-6">
          <div className="md:col-span-7 card-flat p-7 reveal" data-testid="certs-card">
            <div className="overline mb-4">Certifications &amp; Compliance</div>
            <div className="flex flex-wrap gap-2">
              {certs.map((c) => (
                <span key={c} className="badge-outline" style={{ fontSize: 11, padding: "6px 14px" }}>
                  {c}
                </span>
              ))}
            </div>
            <div className="hairline mt-5 pt-4 text-[12px]" style={{ color: "var(--ink-2)" }}>
              Mfg partners certified under <strong style={{ color: "var(--ink)" }}>ISO 14644</strong> (clean room),{" "}
              <strong style={{ color: "var(--ink)" }}>ISO 11135 &amp; ISO 11607</strong> (sterilisation), and{" "}
              <strong style={{ color: "var(--ink)" }}>ISO 13485:2016</strong> medical-device QMS by TUV Rheinland.
            </div>
          </div>
          <div className="md:col-span-5 card-onyx p-7 reveal" data-testid="mission-card">
            <div className="overline" style={{ color: "var(--champagne)" }}>Mission · Beyond Profit</div>
            <h3 className="font-serif-display text-[24px] leading-tight mt-2" style={{ color: "var(--ivory)" }}>
              Manufacturing jobs for <em style={{ color: "var(--champagne)" }}>rural women</em> in our upcoming factory.
            </h3>
            <p className="text-[12px] mt-3" style={{ color: "rgba(247,245,240,0.7)" }}>
              The Factory CapEx unlock isn&apos;t just operating leverage — it&apos;s the founding instrument
              of livelihood for women in rural Maharashtra &amp; Telangana.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function BusinessModel() {
  const flow = [
    { n: "01", h: "R&D-Led Sourcing", d: "17 years of formulation insight drives curation of bottles, jars, droppers, pumps — matched to stability, fragrance load and skin-feel of the buyer's product." },
    { n: "02", h: "B2B Wholesale", d: "Bulk supply to perfumers, indie beauty, contract manufacturers and packaging buyers — flexible MOQs, mixed-SKU dispatch, Pan-India + GCC corridor logistics." },
    { n: "03", h: "Private Label", d: "End-to-end execution: bottle architecture, decoration (frosting, hot-stamp, screen, UV), formulation partner matching, filling and finished-goods delivery." },
    { n: "04", h: "Attar D2C (queued)", d: "Brand identity, formulations and packaging are ready. Soft-launch deferred until first Hyderabad ops hire is onboarded — protecting brand reputation, cash and founder bandwidth." },
  ];

  return (
    <section id="business" className="py-24 md:py-32" style={{ background: "var(--ivory-2)" }} data-testid="business-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-7 reveal">
            <div className="overline mb-4">Business Model</div>
            <h2 className="font-serif-display text-[36px] md:text-[52px] leading-[1.02] tracking-tight">
              A B2B wholesale engine — with an
              <em style={{ color: "var(--bronze)" }}> R&amp;D-backed </em>
              private-label moat.
            </h2>
          </div>
          <div className="md:col-span-5 reveal flex items-end">
            <p className="text-[15px]" style={{ color: "var(--ink-2)" }}>
              BEAUTWELLZ operates as a curated B2B supplier first, then up-sells
              private-label execution, decoration, filling — and now a vertically
              integrated Attar D2C brand from the Hyderabad office.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {flow.map((f, i) => (
            <div key={f.n} className="card-flat p-7 reveal" style={{ transitionDelay: `${i * 60}ms` }} data-testid={`business-step-${f.n}`}>
              <div className="font-serif-display text-[48px] leading-none" style={{ color: "var(--bronze)" }}>{f.n}</div>
              <div className="font-serif-display text-[22px] mt-4" style={{ color: "var(--ink)" }}>{f.h}</div>
              <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "var(--ink-2)" }}>{f.d}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-10">
          <div className="md:col-span-8 card-flat p-8 reveal">
            <div className="overline">Revenue Mix · Indicative FY 2025-26</div>
            <h3 className="font-serif-display text-[26px] mt-2">Where the rupee comes from</h3>
            <div className="mt-6 space-y-5">
              {[
                { l: "Glass &amp; Bottle Wholesale", v: 50 },
                { l: "Private Label Execution", v: 30 },
                { l: "Decoration &amp; Filling", v: 12 },
                { l: "Advisory &amp; R&amp;D Consulting", v: 8 },
              ].map((r) => (
                <div key={r.l}>
                  <div className="flex justify-between text-[13px]">
                    <span style={{ color: "var(--ink-2)" }} dangerouslySetInnerHTML={{ __html: r.l }} />
                    <span className="font-mono-data" style={{ color: "var(--ink)" }}>{r.v}%</span>
                  </div>
                  <div className="h-[6px] mt-1.5" style={{ background: "rgba(26,26,26,0.08)" }}>
                    <div className="h-full" style={{ width: `${r.v}%`, background: "var(--bronze)" }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-4 card-flat p-8 reveal flex flex-col justify-center">
            <div className="flex items-center gap-2 text-[12px]" style={{ color: "var(--bronze)", fontFamily: "Outfit" }}>
              <Users size={14} />
              <span style={{ letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600 }}>Current Team · 1 + 1</span>
            </div>
            <div className="text-[14px] mt-3 leading-relaxed" style={{ color: "var(--ink-2)" }}>
              Humera (Founder) + one warehouse hand in Vasai East. The Hyderabad
              ops hire opens the Attar D2C runway in Q3 — lean by design, built to
              scale on the factory raise.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Financials() {
  const strengths = [
    "Visionary foresight — seeded medical disposables in 2018, validated by COVID 2020",
    "17 yrs corporate Haircare R&amp;D · live product demos across Delhi, Kolkata, Mumbai",
    "₹1Cr+ cumulative revenue FY 2020-26 · debt-free · unfunded · profitable",
    "Built virtually through COVID — website, vendor book, dispatch, customer ops",
    "Runs daily operations through a custom AI-agent dashboard · 10× output with 2-person team",
    "OPC Pvt Ltd · GST in 2 states (MH + TG) · IEC export-ready · 4.3/5 rating · 82% response",
  ];
  const risks = [
    "Quality control variability on bulk dispatches — vendor-level QC SOP needed",
    "Occasional billing &amp; reconciliation mismatches reported by buyers",
    "Single-founder operational dependency — key-person risk",
    "Buyer concentration risk in top-decile accounts",
    "Vendor lead-time volatility from upstream glass houses",
    "Attar D2C is pre-revenue; brand-build budget yet to be deployed",
  ];

  return (
    <section id="financials" className="py-24 md:py-32" data-testid="financials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="reveal mb-14">
          <div className="overline mb-4">Financial Health &amp; Performance</div>
          <h2 className="font-serif-display text-[36px] md:text-[52px] leading-[1.02] tracking-tight max-w-3xl">
            Profitable. Rated. <em style={{ color: "var(--bronze)" }}>Investable.</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          <div className="md:col-span-3 card-flat p-7 reveal" data-testid="metric-rating">
            <div className="overline">Customer Rating</div>
            <div className="flex items-baseline gap-2 mt-3">
              <span className="metric-value text-[64px]" style={{ color: "var(--ink)" }}>4.3</span>
              <span className="font-mono-data text-[18px]" style={{ color: "var(--ink-2)" }}>/ 5</span>
            </div>
            <div className="flex gap-1 mt-2">
              {[1, 2, 3, 4].map((i) => <Star key={i} size={14} fill="var(--bronze)" stroke="var(--bronze)" />)}
              <Star size={14} stroke="var(--bronze)" />
            </div>
            <div className="overline mt-3" style={{ fontSize: 10 }}>400+ verified B2B buyers</div>
          </div>

          <div className="md:col-span-3 card-flat p-7 reveal" data-testid="metric-response">
            <div className="overline">Response Rate</div>
            <div className="metric-value text-[64px] mt-3" style={{ color: "var(--ink)" }}>
              82<span style={{ color: "var(--bronze)" }}>%</span>
            </div>
            <div className="h-[6px] mt-2" style={{ background: "rgba(26,26,26,0.08)" }}>
              <div className="h-full" style={{ width: "82%", background: "var(--bronze)" }} />
            </div>
            <div className="overline mt-3" style={{ fontSize: 10 }}>Category benchmark · 60%</div>
          </div>

          <div className="md:col-span-3 card-flat p-7 reveal" data-testid="metric-revenue">
            <div className="overline">Cumulative Revenue</div>
            <div className="metric-value text-[52px] mt-3 leading-none" style={{ color: "var(--ink)" }}>
              ₹1Cr<span style={{ color: "var(--bronze)" }}>+</span>
            </div>
            <div className="overline mt-3" style={{ fontSize: 10 }}>Feb 2020 – FY 2025-26 · Self-funded</div>
          </div>

          <div className="md:col-span-3 card-flat p-7 reveal" data-testid="metric-structure">
            <div className="overline">Entity</div>
            <div className="font-serif-display text-[28px] mt-3 leading-tight" style={{ color: "var(--ink)" }}>
              OPC Pvt Ltd
            </div>
            <div className="overline mt-3" style={{ fontSize: 10 }}>MCA · GST (MH + TG) · IEC</div>
          </div>
        </div>

        <div className="mt-10 card-flat p-8 reveal" data-testid="turnover-journey">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-7">
              <div className="overline mb-3">The Bootstrapped Climb · Feb 2020 → FY 2025-26</div>
              <h3 className="font-serif-display text-[26px] md:text-[36px] leading-tight" style={{ color: "var(--ink)" }}>
                ₹1 Lakh at incorporation. <em style={{ color: "var(--bronze)" }}>₹1 Crore+</em> turnover today.
              </h3>
              <p className="mt-4 text-[14px] md:text-[15px] leading-relaxed" style={{ color: "var(--ink-2)" }}>
                Humera began with just <strong style={{ color: "var(--ink)" }}>₹1 Lakh</strong> of her own money at incorporation in February 2020 — and never took on debt or outside funding. Through a genuine roller-coaster of personal and health challenges, she kept earning, reinvesting every rupee and self-funding the company forward.
              </p>
              <p className="mt-3 text-[14px] md:text-[15px] leading-relaxed" style={{ color: "var(--ink-2)" }}>
                From that ₹1 Lakh seed, BEAUTWELLZ has crossed <strong style={{ color: "var(--ink)" }}>₹1 Crore+ in cumulative turnover</strong> (Feb 2020 – FY 2025-26), with <strong style={{ color: "var(--ink)" }}>₹34.90 Lakh</strong> in the latest year (FY 2025-26) — every account served strictly no-credit. This is what she has built entirely alone; the ₹3 Cr round is the first outside capital she has ever raised.
              </p>
            </div>
            <div className="md:col-span-5 grid grid-cols-2 gap-4">
              {[
                { v: "₹1 Lakh", l: "Seed at incorporation · Feb 2020" },
                { v: "₹1 Cr+", l: "Cumulative turnover · to FY26" },
                { v: "₹34.90 L", l: "Latest year · FY 2025-26" },
                { v: "Zero", l: "Debt · outside funding" },
              ].map((m) => (
                <div key={m.l} className="p-5" style={{ border: "1px solid rgba(26,26,26,0.1)" }}>
                  <div className="metric-value text-[30px]" style={{ color: "var(--bronze)" }}>{m.v}</div>
                  <div className="overline mt-2" style={{ fontSize: 9 }}>{m.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <div className="card-flat p-8 reveal" data-testid="strengths-card">
            <div className="flex items-center gap-2 mb-5">
              <Award size={16} style={{ color: "var(--bronze)" }} />
              <div className="overline">Strengths</div>
            </div>
            <ul>
              {strengths.map((s) => (
                <li key={s} className="tick-row">
                  <Check size={14} style={{ color: "var(--success)", marginTop: 4 }} />
                  <span className="text-[14px]" style={{ color: "var(--ink-2)" }} dangerouslySetInnerHTML={{ __html: s }} />
                </li>
              ))}
            </ul>
          </div>

          <div className="card-flat p-8 reveal" data-testid="risks-card">
            <div className="flex items-center gap-2 mb-5">
              <AlertTriangle size={16} style={{ color: "var(--danger)" }} />
              <div className="overline">Risks · Watchlist</div>
            </div>
            <ul>
              {risks.map((s) => (
                <li key={s} className="tick-row">
                  <Minus size={14} style={{ color: "var(--danger)", marginTop: 4 }} />
                  <span className="text-[14px]" style={{ color: "var(--ink-2)" }} dangerouslySetInnerHTML={{ __html: s }} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Market() {
  const tamData = [
    { y: "2022", v: 9800 },
    { y: "2023", v: 11200 },
    { y: "2024", v: 13100 },
    { y: "2025", v: 15200 },
    { y: "2026", v: 17900 },
    { y: "2027", v: 21100 },
    { y: "2028", v: 25000 },
  ];

  const compMatrix = [
    { name: "BEAUTWELLZ", curated: "High", privateLabel: "End-to-End", moq: "Flexible", advisory: "Yes · 17 yr R&D", highlight: true },
    { name: "Glass Distributors", curated: "Medium", privateLabel: "None", moq: "High", advisory: "No" },
    { name: "Local Manufacturers", curated: "Low", privateLabel: "Partial", moq: "Very High", advisory: "No" },
    { name: "International Suppliers", curated: "High", privateLabel: "Limited", moq: "Very High", advisory: "No" },
  ];

  return (
    <section id="market" className="py-24 md:py-32" style={{ background: "var(--ivory-2)" }} data-testid="market-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-7 reveal">
            <div className="overline mb-4">Market Opportunity</div>
            <h2 className="font-serif-display text-[36px] md:text-[52px] leading-[1.02] tracking-tight">
              A ₹15,000+ Cr opportunity growing at
              <em style={{ color: "var(--bronze)" }}> 12–25% CAGR.</em>
            </h2>
          </div>
          <div className="md:col-span-5 reveal flex items-end">
            <p className="text-[15px]" style={{ color: "var(--ink-2)" }}>
              India&apos;s fragrance and cosmetic packaging market is in a
              decade-long expansion led by indie beauty, D2C launches, GCC
              fragrance demand and contract manufacturing. The UAE&apos;s
              Generative-AI &amp; new-business government programs add a
              second flywheel — Humera&apos;s AI-agent dashboard build
              positions BEAUTWELLZ as a tech-forward operator eligible for
              those schemes.
            </p>
          </div>
        </div>

        <div className="mb-10 reveal p-6" style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(156,124,56,0.25)" }} data-testid="market-cagr-note">
          <div className="overline mb-2">Scope of these figures</div>
          <p className="text-[13px] md:text-[14px] leading-relaxed" style={{ color: "var(--ink-2)" }}>
            The <strong style={{ color: "var(--ink)" }}>12–25% CAGR</strong> and <strong style={{ color: "var(--ink)" }}>₹15,000+ Cr TAM</strong> above are for the
            <strong style={{ color: "var(--ink)" }}> perfume &amp; cosmetic packaging segment (i-skills)</strong> only — the sole focus of this raise.
            BEAUTWELLZ&apos;s other four verticals (i-dispose, i-hydrate, i-biodegradable, i-construct) each serve separate markets whose growth is
            <strong style={{ color: "var(--ink)" }}> not counted</strong> in these numbers — they sit as additional, untapped upside.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-8 card-flat p-7 reveal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">TAM Trajectory · ₹ Cr</div>
                <div className="font-serif-display text-[24px] mt-1">Fragrance + Cosmetic Packaging Market</div>
              </div>
              <div className="hidden md:flex gap-3">
                <span className="badge-outline">India</span>
                <span className="badge-outline">2022 – 2028E</span>
              </div>
            </div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <AreaChart data={tamData}>
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#9C7C38" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#9C7C38" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(26,26,26,0.06)" vertical={false} />
                  <XAxis dataKey="y" stroke="#4A4A4A" tick={{ fontFamily: "Outfit", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis stroke="#4A4A4A" tick={{ fontFamily: "Outfit", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#121212", border: "none", color: "#F7F5F0", fontFamily: "Outfit", fontSize: 12 }} labelStyle={{ color: "#D4AF37" }} />
                  <Area type="monotone" dataKey="v" stroke="#9C7C38" strokeWidth={2} fill="url(#g1)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="md:col-span-4 grid grid-cols-1 gap-5">
            {[
              { v: "₹15,000+ Cr", l: "Current TAM (2025E)", i: <Target size={16} /> },
              { v: "12–25%", l: "CAGR · 2025-2028", i: <TrendingUp size={16} /> },
              { v: "₹25,000 Cr", l: "Projected by 2028", i: <BarChart3 size={16} /> },
            ].map((m) => (
              <div key={m.l} className="card-flat p-6 reveal" data-testid={`market-stat-${m.l.split(" ")[0].toLowerCase()}`}>
                <div className="flex items-center gap-2 mb-2" style={{ color: "var(--bronze)" }}>
                  {m.i}
                  <div className="overline">{m.l}</div>
                </div>
                <div className="metric-value text-[42px]" style={{ color: "var(--ink)" }}>{m.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 reveal">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="overline">Competitive Landscape</div>
              <h3 className="font-serif-display text-[28px] mt-1">Why BEAUTWELLZ wins the curated middle.</h3>
            </div>
          </div>
          <div className="card-flat overflow-hidden">
            <div className="scroll-x">
              <table className="w-full text-left" style={{ minWidth: 720 }}>
                <thead>
                  <tr className="text-[11px] tracking-[0.18em] uppercase" style={{ color: "var(--ink-2)", fontFamily: "Outfit, sans-serif" }}>
                    <th className="py-4 px-4">Player</th>
                    <th className="py-4 px-4">Curation</th>
                    <th className="py-4 px-4">Private Label</th>
                    <th className="py-4 px-4">MOQ Flex.</th>
                    <th className="py-4 px-4">Advisory</th>
                  </tr>
                </thead>
                <tbody>
                  {compMatrix.map((r) => (
                    <tr key={r.name} style={{ borderTop: "1px solid rgba(26,26,26,0.08)", background: r.highlight ? "rgba(212,175,55,0.08)" : "transparent" }}>
                      <td className="py-5 px-4">
                        <div className="font-serif-display text-[18px]">{r.name}</div>
                        {r.highlight && <span className="badge-outline mt-1">Subject Company</span>}
                      </td>
                      <td className="py-5 px-4 font-mono-data">{r.curated}</td>
                      <td className="py-5 px-4 font-mono-data">{r.privateLabel}</td>
                      <td className="py-5 px-4 font-mono-data">{r.moq}</td>
                      <td className="py-5 px-4 font-mono-data">{r.advisory}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Valuation() {
  const useOfFunds = [
    { k: "Factory Setup · Land, Building &amp; Machinery", v: 45 },
    { k: "Working Capital &amp; Inventory", v: 20 },
    { k: "Team &amp; Operations", v: 20 },
    { k: "Compliance &amp; Reserve", v: 15 },
  ];

  const returns = [
    { s: "Conservative", val: "₹50 Cr", stake: "₹1.5 Cr", back: "₹1.5 Cr", gain: "+₹0.5 Cr", moic: "1.5×", irr: "~15%" },
    { s: "Base Case", val: "₹80 Cr", stake: "₹2.4 Cr", back: "₹2.4 Cr", gain: "+₹1.4 Cr", moic: "2.4×", irr: "~34%", highlight: true },
    { s: "Upside", val: "₹120 Cr", stake: "₹3.6 Cr", back: "₹3.6 Cr", gain: "+₹2.6 Cr", moic: "3.6×", irr: "~53%" },
  ];

  return (
    <section id="valuation" className="py-28 md:py-36 card-onyx grain" style={{ background: "var(--onyx)", color: "var(--ivory)" }} data-testid="valuation-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-7 reveal">
            <div className="overline" style={{ color: "var(--champagne)" }}>Investment Opportunity</div>
            <h2 className="font-serif-display text-[44px] md:text-[68px] leading-[0.98] tracking-tight mt-4" style={{ color: "var(--ivory)" }}>
              Valuation<br />
              <em style={{ color: "var(--champagne)" }}>₹25 — 50 Crore.</em>
            </h2>
            <p className="mt-6 text-[16px] max-w-xl" style={{ color: "rgba(247,245,240,0.7)" }}>
              Indicative range benchmarked against India&apos;s perfume market
              (10–24% CAGR per Grand View &amp; Technavio), perfume packaging
              comparables, BEAUTWELLZ&apos;s founder track record, six-year
              profitability, and the factory-setup CapEx upside.
            </p>
          </div>
          <div className="md:col-span-5 reveal">
            <div className="p-8" style={{ border: "1px solid rgba(212,175,55,0.4)", background: "rgba(212,175,55,0.06)" }}>
              <div className="overline" style={{ color: "var(--champagne)" }}>Suggested Round</div>
              <div className="grid grid-cols-2 gap-5 mt-4">
                <div>
                  <div className="overline" style={{ fontSize: 10, color: "rgba(247,245,240,0.6)" }}>Ask</div>
                  <div className="metric-value text-[42px]" style={{ color: "var(--ivory)" }}>₹1 Cr</div>
                </div>
                <div>
                  <div className="overline" style={{ fontSize: 10, color: "rgba(247,245,240,0.6)" }}>Equity</div>
                  <div className="metric-value text-[42px]" style={{ color: "var(--ivory)" }}>3%</div>
                </div>
                <div>
                  <div className="overline" style={{ fontSize: 10, color: "rgba(247,245,240,0.6)" }}>Implied Valuation</div>
                  <div className="font-mono-data text-[15px] mt-1" style={{ color: "var(--ivory)" }}>~₹33 Cr post-money</div>
                </div>
                <div>
                  <div className="overline" style={{ fontSize: 10, color: "rgba(247,245,240,0.6)" }}>Buy-Back</div>
                  <div className="font-mono-data text-[15px] mt-1" style={{ color: "var(--ivory)" }}>Yr 3 · founder option</div>
                </div>
              </div>
            </div>
            <div className="mt-5 p-5" style={{ border: "1px solid rgba(247,245,240,0.15)" }}>
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={14} style={{ color: "var(--champagne)" }} />
                <div className="overline" style={{ color: "var(--champagne)" }}>Concurrent Channels</div>
              </div>
              <div className="text-[13px]" style={{ color: "rgba(247,245,240,0.8)" }}>
                Brief listed on <strong style={{ color: "var(--ivory)" }}>OpenVC.com</strong> ·
                Application-ready for <strong style={{ color: "var(--ivory)" }}>UAE Generative-AI &amp; new-business government programs</strong> — leveraging Humera&apos;s AI-agent dashboard build.
              </div>
            </div>
          </div>
        </div>

        <div className="mb-14 reveal p-8" style={{ border: "1px solid rgba(212,175,55,0.4)", background: "rgba(212,175,55,0.05)" }} data-testid="valuation-explainer">
          <div className="overline mb-3" style={{ color: "var(--champagne)" }}>How the ₹33 Cr number works · In plain English</div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-5">
              <div className="font-serif-display text-[24px] md:text-[30px] leading-tight" style={{ color: "var(--ivory)" }}>
                ₹33 Cr is a <em style={{ color: "var(--champagne)" }}>valuation</em> — not turnover.
              </div>
              <div className="mt-5 p-5 font-mono-data text-[14px] leading-relaxed" style={{ border: "1px solid rgba(247,245,240,0.15)", color: "rgba(247,245,240,0.9)" }}>
                Investor invests ₹1 Cr → receives 3%<br />
                If ₹1 Cr equals 3% of the company,<br />
                then 100% = ₹1 Cr ÷ 3%<br />
                = <strong style={{ color: "var(--champagne)" }}>~₹33 Cr</strong> post-money valuation
              </div>
            </div>
            <div className="md:col-span-7 text-[14px] md:text-[15px] leading-relaxed" style={{ color: "rgba(247,245,240,0.78)" }}>
              <p className="mb-3">
                <strong style={{ color: "var(--ivory)" }}>Post-money valuation</strong> is what the whole business is worth
                right after the investment. It is <strong style={{ color: "var(--ivory)" }}>not annual sales</strong>, and not the ₹1 Cr+ cumulative revenue earned so far.
              </p>
              <p>
                So why is a bootstrapped, profitable company valued at ₹25–50 Cr? Because the value is <strong style={{ color: "var(--ivory)" }}>forward-looking</strong>. The ₹1 Cr funds an
                owned factory — bringing decoration, filling &amp; QC in-house (30–40% margin uplift + 10× capacity) — to capture a ₹15,000 Cr+ market.
                The investor is buying the <em style={{ color: "var(--champagne)" }}>scaled business ahead</em>, not the trading revenue behind, with a <strong style={{ color: "var(--ivory)" }}>Year-3 founder buy-back</strong> as a clean exit.
              </p>
            </div>
          </div>
          <div className="mt-7 pt-6" style={{ borderTop: "1px solid rgba(247,245,240,0.12)" }} data-testid="valuation-cake">
            <div className="overline mb-4" style={{ color: "var(--champagne)" }}>In simple words · the cake example</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { n: "1", t: "Company = 1 cake", d: "Picture the whole company as one cake cut into 100 equal slices (that's 100%)." },
                { n: "2", t: "Investor buys 3 slices", d: "An investor pays ₹1 Cr and gets 3 slices in return — that is 3% of the company." },
                { n: "3", t: "Price of 1 slice", d: "If 3 slices cost ₹1 Cr, then 1 slice is worth about ₹33 Lakh." },
                { n: "4", t: "Price of the full cake", d: "100 slices × ₹33 Lakh = ₹33 Cr. So the whole company is priced at ₹33 Cr." },
              ].map((s) => (
                <div key={s.n} className="p-4" style={{ border: "1px solid rgba(247,245,240,0.12)" }}>
                  <div className="font-serif-display text-[30px]" style={{ color: "var(--champagne)" }}>{s.n}</div>
                  <div className="font-mono-data text-[14px] mt-1" style={{ color: "var(--ivory)" }}>{s.t}</div>
                  <div className="text-[12px] mt-2 leading-relaxed" style={{ color: "rgba(247,245,240,0.7)" }}>{s.d}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 font-mono-data text-[13px] md:text-[14px]" style={{ color: "rgba(247,245,240,0.85)" }}>
              Simple formula: <strong style={{ color: "var(--champagne)" }}>Valuation = Investment ÷ share%</strong> = ₹1 Cr ÷ 3% (0.03) = <strong style={{ color: "var(--champagne)" }}>≈ ₹33 Cr</strong>. Same logic works in reverse: the ₹33 Cr price is set by the factory-led growth plan, and the 3% simply costs 3% of that price.
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-7 reveal">
            <div className="overline" style={{ color: "var(--champagne)" }}>Growth Levers · Factory-Led</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
              {[
                { h: "Own Factory Setup", d: "End vendor dependency. Decoration, filling & QC brought in-house — 30-40% margin uplift on existing volume." },
                { h: "10× Production Capacity", d: "Capacity unlock for B2B wholesale + private label without proportional opex — operating leverage compounds from Year 2." },
                { h: "Attar D2C (Hyderabad)", d: "Higher-margin D2C line on Telangana GST, gated on first ops hire — adds 15-20% to revenue mix by FY 27." },
                { h: "UAE GenAI Programs", d: "Apply to UAE Generative-AI & new-business government schemes — leveraging the AI-agent dashboard as the tech moat." },
                { h: "Digital Channel Activation", d: "₹45–55L incremental annual revenue from structured SEO + content + GMB stack on top of factory capacity." },
                { h: "Investor Buy-Back · Year 3", d: "Founder retains right to buy back the 3% stake at agreed valuation in Year 3 — clean exit pathway for investor." },
              ].map((g) => (
                <div key={g.h} className="p-5" style={{ border: "1px solid rgba(247,245,240,0.12)" }} data-testid={`lever-${g.h.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="font-serif-display text-[20px]" style={{ color: "var(--ivory)" }} dangerouslySetInnerHTML={{ __html: g.h }} />
                  <div className="text-[13px] mt-2" style={{ color: "rgba(247,245,240,0.65)" }}>{g.d}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5 reveal">
            <div className="overline" style={{ color: "var(--champagne)" }}>Use of Funds · ₹3 Cr Round</div>
            <div className="text-[12px] mt-1 mb-1" style={{ color: "rgba(247,245,240,0.6)" }}>Up to ~45% goes to factory setup.</div>
            <div className="mt-5 space-y-4">
              {useOfFunds.map((r) => (
                <div key={r.k}>
                  <div className="flex justify-between text-[13px]" style={{ color: "rgba(247,245,240,0.8)" }}>
                    <span dangerouslySetInnerHTML={{ __html: r.k }} />
                    <span className="font-mono-data" style={{ color: "var(--champagne)" }}>{r.v}%</span>
                  </div>
                  <div className="h-[6px] mt-1.5" style={{ background: "rgba(247,245,240,0.1)" }}>
                    <div className="h-full" style={{ width: `${r.v}%`, background: "var(--champagne)" }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6" style={{ border: "1px solid rgba(247,245,240,0.15)" }}>
              <div className="overline" style={{ color: "var(--champagne)" }}>Valuation Logic</div>
              <div className="font-mono-data text-[14px] mt-3 leading-relaxed" style={{ color: "rgba(247,245,240,0.85)" }}>
                India perfume market · 10–24% CAGR<br />
                Glass = 38.5% of perfume packaging<br />
                Profitable 6-yr operating history<br />
                Vision-validated founder · COVID call<br />
                Factory CapEx → 30-40% margin uplift<br />
                Suggested band: <strong style={{ color: "var(--champagne)" }}>₹25 – 50 Cr</strong>
              </div>
              <div className="text-[12px] mt-3" style={{ color: "rgba(247,245,240,0.55)" }}>
                Investor enters at ₹33 Cr post-money for 3% equity with a Year-3 founder buy-back option.
              </div>
            </div>

            <div className="mt-5 p-6" style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(212,175,55,0.05)" }} data-testid="attar-d2c-card">
              <div className="overline mb-2" style={{ color: "var(--champagne)" }}>House of BW · Attar D2C (Queued)</div>
              <p className="text-[13px] md:text-[14px] leading-relaxed" style={{ color: "rgba(247,245,240,0.78)" }}>
                BEAUTWELLZ&apos;s own direct-to-consumer attar label — <strong style={{ color: "var(--ivory)" }}>brand identity, formulations &amp; packaging are already built</strong>. Roll-on attars in signature gift pouches, GCC-ready, positioned for the premium fragrance buyer.
              </p>
              <p className="text-[13px] md:text-[14px] leading-relaxed mt-3" style={{ color: "rgba(247,245,240,0.78)" }}>
                Soft-launch is <strong style={{ color: "var(--ivory)" }}>deliberately queued until after the i-skills raise</strong> and the first Hyderabad ops hire — protecting brand reputation, cash and founder bandwidth. A ready-to-fire second engine, not a distraction.
              </p>
            </div>
          </div>
        </div>

        {/* Investor Returns — Illustrative */}
        <div className="mt-16 reveal" data-testid="investor-returns">
          <div className="overline" style={{ color: "var(--champagne)" }}>Investor Returns · Illustrative (3-Year, with Buy-Back)</div>
          <h3 className="font-serif-display text-[28px] md:text-[38px] leading-tight mt-2" style={{ color: "var(--ivory)" }}>
            What ₹1 Cr for 3% <em style={{ color: "var(--champagne)" }}>can return</em> in Year 3.
          </h3>
          <p className="mt-3 max-w-3xl text-[14px] md:text-[15px] leading-relaxed" style={{ color: "rgba(247,245,240,0.72)" }}>
            Round: up to <strong style={{ color: "var(--ivory)" }}>₹3 Cr across 3 investors</strong> (₹1 Cr each for 3% = 9% total),
            entering at the ₹33 Cr post-money valuation; founder retains 91%. At Year 3 the founder exercises the
            <strong style={{ color: "var(--ivory)" }}> buy-back</strong>, repurchasing each 3% stake at its then-fair value — so an
            investor receives their ₹1 Cr back <em>plus</em> the appreciation shown below.
          </p>
          <div className="mt-6 overflow-hidden" style={{ border: "1px solid rgba(247,245,240,0.15)" }}>
            <div className="scroll-x">
              <table className="w-full text-left" style={{ minWidth: 760 }}>
                <thead>
                  <tr className="text-[10px] tracking-[0.14em] uppercase" style={{ color: "var(--champagne)", fontFamily: "Outfit" }}>
                    <th className="py-3 px-4">Scenario</th>
                    <th className="py-3 px-4">Yr-3 Company Value</th>
                    <th className="py-3 px-4">Investor 3% Worth</th>
                    <th className="py-3 px-4">Buy-Back Payout</th>
                    <th className="py-3 px-4">Net Gain</th>
                    <th className="py-3 px-4">MOIC</th>
                    <th className="py-3 px-4">~IRR</th>
                  </tr>
                </thead>
                <tbody>
                  {returns.map((r) => (
                    <tr key={r.s} style={{ borderTop: "1px solid rgba(247,245,240,0.1)", background: r.highlight ? "rgba(212,175,55,0.08)" : "transparent" }} data-testid={`return-${r.s.toLowerCase().replace(/\s+/g, "-")}`}>
                      <td className="py-4 px-4">
                        <span className="font-mono-data text-[14px]" style={{ color: "var(--ivory)" }}>{r.s}</span>
                        {r.highlight && <span className="badge-outline ml-2" style={{ fontSize: 9 }}>Likely</span>}
                      </td>
                      <td className="py-4 px-4 font-mono-data text-[14px]" style={{ color: "rgba(247,245,240,0.85)" }}>{r.val}</td>
                      <td className="py-4 px-4 font-mono-data text-[14px]" style={{ color: "rgba(247,245,240,0.85)" }}>{r.stake}</td>
                      <td className="py-4 px-4 font-mono-data text-[14px]" style={{ color: "var(--champagne)" }}>{r.back}</td>
                      <td className="py-4 px-4 font-mono-data text-[14px]" style={{ color: "var(--champagne)" }}>{r.gain}</td>
                      <td className="py-4 px-4 font-mono-data text-[14px]" style={{ color: "var(--ivory)" }}>{r.moic}</td>
                      <td className="py-4 px-4 font-mono-data text-[14px]" style={{ color: "var(--ivory)" }}>{r.irr}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="mt-3 text-[12px] md:text-[13px]" style={{ color: "rgba(247,245,240,0.6)" }}>
            Bottom line (Base Case): a ₹1 Cr investment returns <strong style={{ color: "var(--champagne)" }}>≈ ₹2.4 Cr</strong> at the Year-3 buy-back — the original ₹1 Cr <strong>plus ≈ ₹1.4 Cr profit</strong> (~2.4× / ~34% IRR). Across all 3 investors, ₹3 Cr in returns ₹6–11 Cr out.
          </div>
        </div>

        {/* Founder Alignment & Outcome */}
        <div className="mt-10 reveal p-8" style={{ border: "1px solid rgba(212,175,55,0.4)", background: "rgba(212,175,55,0.05)" }} data-testid="founder-outcome">
          <div className="overline mb-3" style={{ color: "var(--champagne)" }}>Founder Alignment · What Humera Retains</div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-6 text-[14px] md:text-[15px] leading-relaxed" style={{ color: "rgba(247,245,240,0.78)" }}>
              <p className="mb-3">
                For six years Humera has invested <strong style={{ color: "var(--ivory)" }}>sweat equity + reinvested profits</strong> —
                bootstrapping to ₹1 Cr+ cumulative revenue, debt-free and unfunded — while building the IP
                (i-hydrate, i-dispose, i-biodegradable, i-skills, i-construct), the 15+ client book, the vendor network
                and the AI-agent dashboard that runs the company. That is her capital already in the business.
              </p>
              <p>
                This round dilutes only <strong style={{ color: "var(--ivory)" }}>9%</strong>. Humera keeps
                <strong style={{ color: "var(--ivory)" }}> 91% ownership + board control</strong>, draws a market-standard
                founder-CEO salary as the company scales, and holds the <strong style={{ color: "var(--ivory)" }}>Year-3
                buy-back right</strong> — a clean path back toward ~100% ownership once investors are repaid with profit.
              </p>
            </div>
            <div className="md:col-span-6">
              <div className="overline mb-3" style={{ fontSize: 10, color: "rgba(247,245,240,0.6)" }}>Founder 91% Stake · Illustrative Yr-3 Paper Value</div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { s: "Conservative", v: "₹45 Cr" },
                  { s: "Base", v: "₹73 Cr" },
                  { s: "Upside", v: "₹109 Cr" },
                ].map((f) => (
                  <div key={f.s} className="p-4" style={{ border: "1px solid rgba(247,245,240,0.15)" }}>
                    <div className="metric-value text-[24px]" style={{ color: "var(--champagne)" }}>{f.v}</div>
                    <div className="overline mt-1" style={{ fontSize: 9, color: "rgba(247,245,240,0.6)" }}>{f.s}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 grid grid-cols-1 gap-2 text-[13px]" style={{ color: "rgba(247,245,240,0.78)" }}>
                <div className="flex gap-2"><span style={{ color: "var(--champagne)" }}>·</span> 91% equity + Director-level board control</div>
                <div className="flex gap-2"><span style={{ color: "var(--champagne)" }}>·</span> Market-standard founder-CEO salary (scales with revenue)</div>
                <div className="flex gap-2"><span style={{ color: "var(--champagne)" }}>·</span> Year-3 buy-back right → path to ~100% ownership</div>
                <div className="flex gap-2"><span style={{ color: "var(--champagne)" }}>·</span> Retains all IP, brand &amp; vendor relationships</div>
              </div>
            </div>
          </div>
          <div className="mt-6 pt-4 text-[11px] leading-relaxed" style={{ borderTop: "1px solid rgba(247,245,240,0.12)", color: "rgba(247,245,240,0.5)" }}>
            <strong style={{ color: "rgba(247,245,240,0.7)" }}>Illustrative only.</strong> Figures are scenario projections based on the stated
            assumptions, India perfume/packaging market CAGR (10–24%) and factory-led operating leverage. They are not a guarantee of returns or
            valuation; actual outcomes depend on execution, market conditions and due diligence.
          </div>
        </div>
      </div>
    </section>
  );
}

function DigitalStrategy() {
  const audit = [
    { ch: "Website", st: "Static · Outdated", ga: "No schema, slow CWV, weak CTAs" },
    { ch: "Google / GMB", st: "Unverified", ga: "Missing categories, no reviews flow, no posts" },
    { ch: "IndiaMART", st: "Active · Strong", ga: "Catalog under-utilised, leads not nurtured" },
    { ch: "LinkedIn (Personal)", st: "Active · Founder-led", ga: "No company page, no thought-leadership cadence" },
    { ch: "Instagram", st: "Sparse", ga: "No brand grid, no Reels, no UGC pipeline" },
    { ch: "Amazon Business", st: "Absent", ga: "Untapped B2B procurement channel" },
    { ch: "OpenVC.com", st: "Not Listed", ga: "Listing is queued (this brief) — investor reach" },
    { ch: "Email Marketing", st: "Absent", ga: "No nurture, no quote follow-ups, no win-back" },
  ];

  const keywords = {
    "High-Intent": ["perfume bottles wholesale India", "glass bottle manufacturer for cosmetics", "private label perfume manufacturer", "buy attar bottles bulk online"],
    "Mid-Funnel": ["best cosmetic packaging suppliers India", "MOQ for perfume bottles", "cosmetic jar with pump bulk", "fragrance bottle decoration services"],
    "Long-Tail": ["how to start a perfume brand in India", "MOQ-friendly private label skincare partner", "frosted glass attar bottle 12ml supplier Mumbai", "indie beauty brand packaging consultant Hyderabad"],
  };

  const roiData = [
    { m: "M1", spend: 1.28, rev: 0.6 }, { m: "M2", spend: 1.28, rev: 1.4 },
    { m: "M3", spend: 1.28, rev: 2.4 }, { m: "M4", spend: 1.28, rev: 3.2 },
    { m: "M5", spend: 1.28, rev: 4.0 }, { m: "M6", spend: 1.28, rev: 4.6 },
    { m: "M7", spend: 1.28, rev: 5.0 }, { m: "M8", spend: 1.28, rev: 5.4 },
    { m: "M9", spend: 1.28, rev: 5.6 }, { m: "M10", spend: 1.28, rev: 5.8 },
    { m: "M11", spend: 1.28, rev: 6.0 }, { m: "M12", spend: 1.28, rev: 6.2 },
  ];

  const channels = [
    { h: "Website Overhaul", d: "Schema-rich, CWV-optimised, lead-gen first." },
    { h: "Google My Business", d: "Verify both Mumbai &amp; Hyderabad pins · review velocity · weekly posts." },
    { h: "LinkedIn", d: "Founder-led thought leadership + company page." },
    { h: "Instagram", d: "Tactile reels — glass, decoration, factory floor." },
    { h: "Amazon Business", d: "B2B procurement listing with bulk pricing." },
    { h: "Email Nurture", d: "Quote follow-ups · seasonal launches · winbacks." },
  ];

  const quickWins = [
    "Verify both GMB pins (Mumbai HQ &amp; Hyderabad office) with 6 categories",
    "Set up reviews ask-SOP across IndiaMART + GMB (target 25 reviews / 30d)",
    "Launch BEAUTWELLZ LinkedIn company page + 8 founder posts in 30d",
    "Publish 4 cornerstone blogs targeting high-intent keywords",
    "Implement schema markup (Organization, Product, FAQ, Breadcrumb)",
    "Compress hero + product images, hit CWV green on mobile",
    "Set up email nurture for IndiaMART leads (Day 0/3/7/14)",
    "Submit OpenVC.com listing + UAE GenAI program application",
  ];

  return (
    <section id="digital" className="py-24 md:py-32" data-testid="digital-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="reveal mb-14">
          <div className="overline mb-4">Digital Strategy &amp; SEO Enhancement</div>
          <h2 className="font-serif-display text-[36px] md:text-[52px] leading-[1.02] tracking-tight max-w-3xl">
            ₹1.28 Lakh / month — to unlock
            <em style={{ color: "var(--bronze)" }}> ₹45–55 Lakhs </em>
            of annual revenue.
          </h2>
        </div>

        <div className="reveal mb-14">
          <div className="flex items-center gap-2 mb-4">
            <Globe size={16} style={{ color: "var(--bronze)" }} />
            <div className="overline">Current Digital Footprint Audit</div>
          </div>
          <div className="card-flat overflow-hidden">
            <div className="scroll-x">
              <table className="w-full text-left" style={{ minWidth: 720 }}>
                <thead>
                  <tr className="text-[11px] tracking-[0.18em] uppercase" style={{ color: "var(--ink-2)", fontFamily: "Outfit" }}>
                    <th className="py-4 px-4">Channel</th>
                    <th className="py-4 px-4">Current State</th>
                    <th className="py-4 px-4">Identified Gaps</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((r) => (
                    <tr key={r.ch} style={{ borderTop: "1px solid rgba(26,26,26,0.08)" }}>
                      <td className="py-4 px-4 font-serif-display text-[17px]">{r.ch}</td>
                      <td className="py-4 px-4 font-mono-data text-[13px]">{r.st}</td>
                      <td className="py-4 px-4 text-[13px]" style={{ color: "var(--ink-2)" }}>{r.ga}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
          {Object.entries(keywords).map(([bucket, list], i) => (
            <div key={bucket} className="card-flat p-6 reveal" style={{ transitionDelay: `${i * 80}ms` }}>
              <div className="flex items-center gap-2 mb-3">
                <Search size={14} style={{ color: "var(--bronze)" }} />
                <div className="overline">{bucket} Keywords</div>
              </div>
              <ul className="space-y-2.5">
                {list.map((k) => (
                  <li key={k} className="flex items-start gap-2 text-[13px]" style={{ color: "var(--ink-2)" }}>
                    <ChevronRight size={14} style={{ color: "var(--bronze)", marginTop: 3, flexShrink: 0 }} />
                    <span>{k}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-14">
          <div className="md:col-span-7 card-flat p-7 reveal">
            <div className="overline mb-2">Content Marketing</div>
            <h3 className="font-serif-display text-[26px] mb-5">Quick wins → durable assets</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <div className="overline" style={{ fontSize: 10 }}>0 – 90 days</div>
                <ul className="mt-3 space-y-2 text-[13px]" style={{ color: "var(--ink-2)" }}>
                  <li>· 12 SEO-led blog posts (1,500+ words)</li>
                  <li>· 8 packaging close-up reels for Instagram</li>
                  <li>· 4 buyer case studies as PDFs</li>
                  <li>· &quot;Packaging Buyer&apos;s Guide&quot; cornerstone</li>
                </ul>
              </div>
              <div>
                <div className="overline" style={{ fontSize: 10 }}>90 – 365 days</div>
                <ul className="mt-3 space-y-2 text-[13px]" style={{ color: "var(--ink-2)" }}>
                  <li>· Quarterly &quot;Start Your Beauty Brand&quot; webinar</li>
                  <li>· Indie Beauty White Paper (annual)</li>
                  <li>· Founder-led LinkedIn newsletter</li>
                  <li>· Long-form YouTube factory tours</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="md:col-span-5 card-flat p-7 reveal">
            <div className="overline mb-2">Multi-Channel Stack</div>
            <h3 className="font-serif-display text-[26px] mb-5">Six channels, one funnel.</h3>
            <ul className="space-y-3">
              {channels.map((c) => (
                <li key={c.h} className="flex items-start gap-3">
                  <span className="divider-dot mt-2" />
                  <div>
                    <div className="font-mono-data text-[14px]" style={{ color: "var(--ink)" }}>{c.h}</div>
                    <div className="text-[12px]" style={{ color: "var(--ink-2)" }} dangerouslySetInnerHTML={{ __html: c.d }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="card-flat p-7 reveal mb-14">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <div className="overline">Budget vs Revenue Impact · 12 Months</div>
              <h3 className="font-serif-display text-[28px] mt-1">₹1.28L / mo investment · 4–6× Year 1 ROI</h3>
            </div>
            <div className="flex gap-4 text-[12px]" style={{ fontFamily: "Outfit" }}>
              <span className="flex items-center gap-2">
                <span style={{ width: 16, height: 3, background: "rgba(26,26,26,0.4)", display: "inline-block" }} />
                Investment (₹ Lakhs)
              </span>
              <span className="flex items-center gap-2">
                <span style={{ width: 16, height: 3, background: "var(--bronze)", display: "inline-block" }} />
                Incremental Revenue (₹ Lakhs)
              </span>
            </div>
          </div>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={roiData} barCategoryGap={12}>
                <CartesianGrid stroke="rgba(26,26,26,0.06)" vertical={false} />
                <XAxis dataKey="m" stroke="#4A4A4A" tick={{ fontFamily: "Outfit", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis stroke="#4A4A4A" tick={{ fontFamily: "Outfit", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#121212", border: "none", color: "#F7F5F0", fontFamily: "Outfit", fontSize: 12 }} labelStyle={{ color: "#D4AF37" }} />
                <Bar dataKey="spend" fill="rgba(26,26,26,0.35)" />
                <Bar dataKey="rev" fill="#9C7C38" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-8">
            {[
              { v: "₹15.4 L", l: "Annual Investment" },
              { v: "₹45-55 L", l: "Revenue Impact / yr" },
              { v: "4-6×", l: "Year 1 ROI" },
              { v: "8-10 mo", l: "Payback Period" },
            ].map((m) => (
              <div key={m.l}>
                <div className="metric-value text-[34px]" style={{ color: "var(--ink)" }}>{m.v}</div>
                <div className="overline mt-1" style={{ fontSize: 10 }}>{m.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-flat p-7 reveal">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} style={{ color: "var(--bronze)" }} />
            <div className="overline">Quick Wins · First 30 Days</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
            {quickWins.map((q, i) => (
              <div key={q} className="tick-row">
                <span className="font-mono-data text-[12px]" style={{ color: "var(--bronze)" }}>{String(i + 1).padStart(2, "0")}</span>
                <span className="text-[14px]" style={{ color: "var(--ink-2)" }} dangerouslySetInnerHTML={{ __html: q }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Roadmap() {
  const quarters = [
    { q: "Q1 · Foundation", m: "Months 1 – 3", items: ["Website rebuild · schema · CWV green", "GMB verification (Mumbai + Hyderabad)", "Cornerstone content (12 posts) shipped", "LinkedIn company page + founder cadence", "Hyderabad ops + brand-build hires opened"] },
    { q: "Q2 · Team Build", m: "Months 4 – 6", items: ["First Hyderabad hire onboarded · SOPs documented", "Vendor QC SOP rollout · pilot 3 vendors", "Instagram tactile reels grid (60 posts)", "Programmatic SKU long-tail pages", "Webinar #1 · Start Your Beauty Brand"] },
    { q: "Q3 · Attar D2C Soft-Launch", m: "Months 7 – 9", items: ["Attar D2C brand identity locked", "Soft-launch on D2C + Amazon (Hyderabad-led)", "UAE Generative-AI program submission", "GCC outbound · 100 export-ready leads", "AI-agent dashboard v2 (sales + content)"] },
    { q: "Q4 · Scale", m: "Months 10 – 12", items: ["₹45-55L incremental revenue realised", "OpenVC.com profile · 3 active investor convos", "Top-of-funnel doubling · 30k monthly visits", "Series A / Venture Debt readiness dossier", "Year-2 roadmap drafted with board"] },
  ];

  return (
    <section id="roadmap" className="py-24 md:py-32" style={{ background: "var(--ivory-2)" }} data-testid="roadmap-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="reveal mb-14">
          <div className="overline mb-4">12-Month Implementation</div>
          <h2 className="font-serif-display text-[36px] md:text-[52px] leading-[1.02] tracking-tight max-w-3xl">
            From cottage cadence to a <em style={{ color: "var(--bronze)" }}>boardroom-ready </em>
            operating rhythm.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {quarters.map((q, i) => (
            <div key={q.q} className={`timeline-step reveal ${i === quarters.length - 1 ? "last" : ""}`} style={{ transitionDelay: `${i * 80}ms` }} data-testid={`roadmap-${q.q.split(" ")[0].toLowerCase()}`}>
              <div className="font-serif-display text-[26px]" style={{ color: "var(--ink)" }}>{q.q}</div>
              <div className="overline mt-1" style={{ fontSize: 10 }}>{q.m}</div>
              <ul className="mt-4 space-y-2.5">
                {q.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-[13px]" style={{ color: "var(--ink-2)" }}>
                    <span className="divider-dot mt-2" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RiskMatrix() {
  const risks = [
    { r: "Quality control variability on bulk dispatches", i: "High", p: "Medium", m: "Tiered vendor QC SOP + pre-shipment inspection + photo proofs." },
    { r: "Billing &amp; reconciliation disputes", i: "Medium", p: "Medium", m: "Move to Zoho Books / Tally Prime with auto-reconciliation &amp; e-invoicing." },
    { r: "Single-founder key-person risk", i: "High", p: "Medium", m: "Hyderabad ops hire (Q2) + AI-agent dashboard documents SOPs; cross-train Vasai staff." },
    { r: "Vendor lead-time volatility", i: "Medium", p: "High", m: "Dual-source top 20 SKUs · safety stock for fast movers · contractual SLAs." },
    { r: "Buyer concentration risk", i: "Medium", p: "Medium", m: "Cap top-buyer share at 18% · diversify into Tier-2 indie beauty + GCC." },
    { r: "Attar D2C launch deferred to Q3 post-hire", i: "Low", p: "Medium", m: "Soft-launch only after Hyderabad ops in place — protects brand reputation &amp; cash." },
  ];

  const pill = (lvl) => {
    const cls = lvl === "High" ? "pill pill-high" : lvl === "Medium" ? "pill pill-med" : "pill pill-low";
    return <span className={cls}>{lvl}</span>;
  };

  return (
    <section id="risk" className="py-24 md:py-32" data-testid="risk-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="reveal mb-12">
          <div className="overline mb-4">Risk Assessment &amp; Mitigation</div>
          <h2 className="font-serif-display text-[36px] md:text-[52px] leading-[1.02] tracking-tight max-w-3xl">
            Six risks. <em style={{ color: "var(--bronze)" }}>Six mitigations.</em> Zero hand-waving.
          </h2>
        </div>

        <div className="card-flat overflow-hidden reveal">
          <div className="scroll-x">
            <table className="w-full text-left" style={{ minWidth: 820 }}>
              <thead>
                <tr className="text-[11px] tracking-[0.18em] uppercase" style={{ color: "var(--ink-2)", fontFamily: "Outfit" }}>
                  <th className="py-4 px-4">Risk</th>
                  <th className="py-4 px-4">Impact</th>
                  <th className="py-4 px-4">Probability</th>
                  <th className="py-4 px-4">Mitigation</th>
                </tr>
              </thead>
              <tbody>
                {risks.map((r) => (
                  <tr key={r.r} className="risk-row" style={{ borderTop: "1px solid rgba(26,26,26,0.08)" }}>
                    <td><div className="font-serif-display text-[16px]" style={{ color: "var(--ink)" }} dangerouslySetInnerHTML={{ __html: r.r }} /></td>
                    <td>{pill(r.i)}</td>
                    <td>{pill(r.p)}</td>
                    <td><span className="text-[13px]" style={{ color: "var(--ink-2)" }} dangerouslySetInnerHTML={{ __html: r.m }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function Highlights() {
  const h = [
    { k: "Valuation Range", v: "₹25 – 50 Cr", n: "Industry-benchmarked · Factory-led" },
    { k: "Round", v: "₹1 Cr · 3%", n: "Year-3 buy-back option" },
    { k: "Market CAGR", v: "10 – 24%", n: "India perfume · Grand View / Technavio" },
    { k: "Vision Validated", v: "2018 → 2020", n: "Right early once · likely again" },
  ];
  return (
    <section className="py-20 md:py-24" style={{ background: "var(--ivory)" }} data-testid="highlights-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="reveal mb-10">
          <div className="overline mb-3">Key Investment Highlights</div>
          <h2 className="font-serif-display text-[34px] md:text-[44px] leading-tight max-w-3xl">
            The four numbers an investor needs to remember.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {h.map((x) => (
            <div key={x.k} className="card-flat p-7 reveal" data-testid={`highlight-${x.k.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="overline">{x.k}</div>
              <div className="metric-value text-[48px] mt-3" style={{ color: "var(--ink)" }}>{x.v}</div>
              <div className="text-[12px] mt-2" style={{ color: "var(--ink-2)" }}>{x.n}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Hiring() {
  return (
    <section
      id="hiring"
      className="py-24 md:py-32"
      style={{ background: "var(--ivory-2)" }}
      data-testid="hiring-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5 reveal">
            <div className="overline mb-4">We&apos;re Hiring · Hyderabad</div>
            <h2
              className="font-serif-display text-[36px] md:text-[52px] leading-[1.02] tracking-tight"
              style={{ color: "var(--ink)" }}
            >
              The next hand on
              <em style={{ color: "var(--bronze)" }}> the runway.</em>
            </h2>
            <p
              className="mt-5 text-[15px] leading-relaxed"
              style={{ color: "var(--ink-2)" }}
            >
              One hire unlocks the entire Attar D2C launch and de-risks the
              founder-dependency in our deck. We&apos;re looking for an
              operator who can own the Hyderabad office end-to-end —
              quotes, dispatch, content, customer follow-ups, working
              alongside Humera and our AI-agent dashboard.
            </p>
            <a
              href="#contact"
              className="btn-primary mt-8"
              data-testid="apply-button"
            >
              Apply via Contact Form <ArrowRight size={14} />
            </a>
          </div>

          <div className="md:col-span-7 reveal">
            <div className="card-flat p-8">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                  <div className="overline">Open Role · Full-Time</div>
                  <div
                    className="font-serif-display text-[28px] mt-1"
                    style={{ color: "var(--ink)" }}
                  >
                    Brand &amp; Operations Associate
                  </div>
                </div>
                <span className="badge-outline">Tolichowki · Hyderabad</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2 mb-6">
                {[
                  { k: "Type", v: "Full-time · On-site" },
                  { k: "Experience", v: "2 – 5 yrs preferred" },
                  { k: "Start", v: "Immediate joiners welcome" },
                  { k: "Compensation", v: "Competitive · Discussed in person" },
                ].map((r) => (
                  <div key={r.k} className="py-2" style={{ borderBottom: "1px dashed rgba(26,26,26,0.08)" }}>
                    <div className="overline" style={{ fontSize: 10 }}>{r.k}</div>
                    <div className="font-mono-data text-[14px] mt-0.5" style={{ color: "var(--ink)" }}>{r.v}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <div className="overline mb-2">What you&apos;ll own</div>
                  <ul className="space-y-2 text-[13px]" style={{ color: "var(--ink-2)" }}>
                    <li className="flex gap-2"><Check size={14} style={{ color: "var(--bronze)", marginTop: 3, flexShrink: 0 }} /> Quote-to-dispatch cycle for B2B buyers</li>
                    <li className="flex gap-2"><Check size={14} style={{ color: "var(--bronze)", marginTop: 3, flexShrink: 0 }} /> Hyderabad office &amp; vendor coordination</li>
                    <li className="flex gap-2"><Check size={14} style={{ color: "var(--bronze)", marginTop: 3, flexShrink: 0 }} /> Content + Instagram + LinkedIn cadence</li>
                    <li className="flex gap-2"><Check size={14} style={{ color: "var(--bronze)", marginTop: 3, flexShrink: 0 }} /> Customer follow-ups · IndiaMART nurture</li>
                    <li className="flex gap-2"><Check size={14} style={{ color: "var(--bronze)", marginTop: 3, flexShrink: 0 }} /> Attar D2C launch execution alongside Humera</li>
                  </ul>
                </div>
                <div>
                  <div className="overline mb-2">What we&apos;re looking for</div>
                  <ul className="space-y-2 text-[13px]" style={{ color: "var(--ink-2)" }}>
                    <li className="flex gap-2"><ChevronRight size={14} style={{ color: "var(--bronze)", marginTop: 3, flexShrink: 0 }} /> Beauty / packaging / D2C background a plus</li>
                    <li className="flex gap-2"><ChevronRight size={14} style={{ color: "var(--bronze)", marginTop: 3, flexShrink: 0 }} /> Hindi + English fluency · Telugu welcome</li>
                    <li className="flex gap-2"><ChevronRight size={14} style={{ color: "var(--bronze)", marginTop: 3, flexShrink: 0 }} /> Comfortable using AI tools day-to-day</li>
                    <li className="flex gap-2"><ChevronRight size={14} style={{ color: "var(--bronze)", marginTop: 3, flexShrink: 0 }} /> Bias for ownership over instruction</li>
                    <li className="flex gap-2"><ChevronRight size={14} style={{ color: "var(--bronze)", marginTop: 3, flexShrink: 0 }} /> Hyderabad-based or willing to relocate</li>
                  </ul>
                </div>
              </div>

              <div className="mt-7 p-4" style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(156,124,56,0.25)" }}>
                <div className="text-[13px]" style={{ color: "var(--ink-2)" }}>
                  <strong style={{ color: "var(--ink)" }}>To apply:</strong>{" "}
                  Use the contact form below — mention <em>&quot;Hyderabad Role&quot;</em> in the message and attach your LinkedIn / CV link in the message body.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", org: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await api.post("/enquiries", form);
      setStatus("sent");
      setForm({ name: "", email: "", org: "", message: "" });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Submission failed. Please try again.");
      setStatus("idle");
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 grain" style={{ background: "var(--onyx)", color: "var(--ivory)" }} data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6 reveal">
            <div className="overline" style={{ color: "var(--champagne)" }}>Connect</div>
            <h2 className="font-serif-display text-[42px] md:text-[60px] leading-[0.98] tracking-tight mt-3" style={{ color: "var(--ivory)" }}>
              Let&apos;s talk<br />
              <em style={{ color: "var(--champagne)" }}>numbers.</em>
            </h2>
            <p className="mt-5 max-w-md text-[15px]" style={{ color: "rgba(247,245,240,0.7)" }}>
              For investor decks, banker NDAs, term sheets or strategic
              partnerships — reach Humera directly. All conversations held in
              confidence.
            </p>

            <div className="mt-10 space-y-4">
              <a href="https://www.linkedin.com/in/humerarindani-bw-glass-perfume-bottles-packaging-private-labeling-cosmetic-evangelist" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-[15px] hover:translate-x-1 transition-transform" style={{ color: "var(--ivory)" }} data-testid="linkedin-link">
                <Linkedin size={18} style={{ color: "var(--champagne)" }} />
                LinkedIn · Humera Rindani
                <ArrowUpRight size={14} />
              </a>
              <a href="tel:+917718015108" className="flex items-center gap-3 text-[15px] hover:translate-x-1 transition-transform" style={{ color: "var(--ivory)" }} data-testid="phone-link">
                <Beaker size={18} style={{ color: "var(--champagne)" }} />
                +91 77180 15108
                <ArrowUpRight size={14} />
              </a>
              <a href="mailto:humera.rindani@beautywellness.in" className="flex items-center gap-3 text-[15px] hover:translate-x-1 transition-transform" style={{ color: "var(--ivory)" }} data-testid="email-link">
                <Mail size={18} style={{ color: "var(--champagne)" }} />
                humera.rindani@beautywellness.in
                <ArrowUpRight size={14} />
              </a>
              <div className="flex items-start gap-3 text-[14px]" style={{ color: "rgba(247,245,240,0.8)" }}>
                <MapPin size={18} style={{ color: "var(--champagne)", marginTop: 2 }} />
                <div>
                  <div>Mumbai · MH (Registered Office)</div>
                  <div>Vasai, Palghar · MH (Warehouse)</div>
                  <div>Tolichowki · Hyderabad · TG (Operating Office)</div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-6 reveal">
            <form onSubmit={submit} className="p-8" style={{ border: "1px solid rgba(247,245,240,0.18)" }} data-testid="contact-form">
              <div className="overline mb-5" style={{ color: "var(--champagne)" }}>Request Investor Brief</div>
              <div className="space-y-4">
                {[
                  { k: "name", l: "Full Name" },
                  { k: "email", l: "Email", type: "email" },
                  { k: "org", l: "Organisation / Fund" },
                ].map((f) => (
                  <div key={f.k}>
                    <label className="overline" style={{ fontSize: 10, color: "rgba(247,245,240,0.6)" }}>{f.l}</label>
                    <input
                      type={f.type || "text"}
                      required={f.k !== "org"}
                      value={form[f.k]}
                      onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                      className="w-full bg-transparent py-2.5 text-[15px] focus:outline-none"
                      style={{ borderBottom: "1px solid rgba(247,245,240,0.25)", color: "var(--ivory)", fontFamily: "Outfit" }}
                      data-testid={`contact-input-${f.k}`}
                    />
                  </div>
                ))}
                <div>
                  <label className="overline" style={{ fontSize: 10, color: "rgba(247,245,240,0.6)" }}>Message</label>
                  <textarea
                    rows={4}
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-transparent py-2.5 text-[15px] focus:outline-none resize-none"
                    style={{ borderBottom: "1px solid rgba(247,245,240,0.25)", color: "var(--ivory)", fontFamily: "Outfit" }}
                    data-testid="contact-input-message"
                  />
                </div>
                {error && (
                  <div className="text-[13px]" style={{ color: "#e89191" }} data-testid="contact-error">{error}</div>
                )}
                {status === "sent" && (
                  <div className="text-[13px]" style={{ color: "var(--champagne)" }} data-testid="contact-success">
                    Thank you. Humera will respond within 1–2 business days. Check your inbox for confirmation.
                  </div>
                )}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="btn-primary btn-onyx w-full justify-center"
                    data-testid="contact-form-submit"
                  >
                    {status === "sending" ? "Sending..." : status === "sent" ? "Sent · Submit Another" : "Submit Enquiry"} <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-10" style={{ background: "var(--onyx)", borderTop: "1px solid rgba(247,245,240,0.08)" }} data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3" style={{ color: "rgba(247,245,240,0.7)" }}>
          <Shield size={14} />
          <span className="text-[12px]" style={{ fontFamily: "Outfit", letterSpacing: "0.06em" }}>
            BEAUTWELLZ OPC PVT LTD · Confidential Investor Brief · 2026
          </span>
        </div>
        <div className="text-[11px]" style={{ color: "rgba(247,245,240,0.5)", fontFamily: "Outfit", letterSpacing: "0.12em", textTransform: "uppercase" }}>
          Prepared for prospective investors &amp; bankers
        </div>
      </div>
    </footer>
  );
}

export default function Home() {
  useReveal();
  useEffect(() => {
    try {
      if (localStorage.getItem("bw_token")) return; // don't count admin's own visits
      const last = Number(sessionStorage.getItem("bw_view_ts") || 0);
      if (Date.now() - last < 30 * 60 * 1000) return; // once per 30 min per session
      sessionStorage.setItem("bw_view_ts", String(Date.now()));
      api
        .post("/analytics/track", {
          type: "page_view",
          path: window.location.pathname,
          referrer: document.referrer || "",
        })
        .catch(() => {});
    } catch (e) {}
  }, []);
  return (
    <div className="App" data-testid="app-root">
      <Header />
      <Hero />
      <Founder />
      <Journey />
      <BuyerVoices />
      <BrandHouse />
      <BusinessModel />
      <Financials />
      <Market />
      <Valuation />
      <DigitalStrategy />
      <Roadmap />
      <RiskMatrix />
      <Highlights />
      <Hiring />
      <Contact />
      <Footer />
    </div>
  );
}
